import { useAuthStore } from "../stores/authStore.ts";
import {
  handleTokenExpiration,
  handlePermissionError,
  isAuthEndpoint,
  createNotificationHandler,
} from "./authUtils";
import i18n from "../i18n";
import { ErrorMessages } from "./i18nUtils";
import { logAppError } from "../utils/errorHandler";

/**
 * Maps backend error messages (English) to translated frontend messages
 * This is needed because the backend doesn't have i18n support yet
 */
/**
 * Backend refusals that carry a stable `error_code`.
 *
 * Preferred over matching on the English prose below: the code is contractual
 * and the sentence is not, so a reworded backend message cannot silently drop
 * a translation back to English. A code this build does not know falls through
 * to the message, so the backend can add one without waiting for a release.
 */
const ERROR_CODE_KEYS: Record<string, string> = {
  cover_run_already_started: "covers.errors.runAlreadyStarted",
  cover_already_covered: "covers.errors.alreadyCovered",
  cover_starts_in_past: "covers.errors.startsInPast",
  cover_own_journey: "covers.errors.ownJourney",
  cover_already_decided: "covers.errors.alreadyDecided",
  cover_not_active: "covers.errors.notActive",
  cover_request_past: "covers.errors.requestPast",
  cover_journey_covered: "covers.errors.journeyCovered",
  journey_inactive: "covers.errors.journeyInactive",
  journey_no_supervisor: "covers.errors.journeyNoSupervisor",
  // Cities & areas (مدينة / منطقة)
  city_name_taken: "cityAreas.errors.cityNameTaken",
  area_name_taken: "cityAreas.errors.areaNameTaken",
  city_has_areas: "cityAreas.errors.cityHasAreas",
  city_in_use: "cityAreas.errors.cityInUse",
  area_in_use: "cityAreas.errors.areaInUse",
  // Finance usage policy (the acceptance gate in front of Financial Management)
  finance_terms_outdated: "finance.terms.errors.outdated",
  finance_terms_pending: "finance.terms.errors.pending",
  // Money guards: one writer per parent, a payment never past what is owed.
  finance_family_busy: "finance.errors.familyBusy",
  finance_allocation_exceeds_due: "finance.errors.allocationExceedsDue",
  finance_total_below_reductions: "finance.errors.totalBelowReductions",
  finance_request_key_mismatch: "finance.errors.requestKeyMismatch",
  city_limit_reached: "cityAreas.errors.cityLimit",
  area_limit_reached: "cityAreas.errors.areaLimit",
  location_unknown: "cityAreas.errors.locationUnknown",
  area_city_mismatch: "cityAreas.errors.areaCityMismatch",
  // Customers (العملاء) and sales agents
  customer_decline_reason_required: "customers.errors.declineReasonRequired",
  customer_unknown_place: "customers.errors.unknownPlace",
  customer_unknown_owner: "customers.errors.unknownOwner",
  customer_archived: "customers.errors.archived",
  customer_edit_conflict: "customers.errors.editConflict",
  customer_school_already_linked: "customers.errors.schoolAlreadyLinked",
  customer_submission_already_converted: "customers.errors.submissionAlreadyConverted",
  customer_agent_phone_in_use: "salesAgents.errors.phoneInUse",
  sales_agent_not_a_school_role: "salesAgents.errors.notASchoolRole",
  // Ramadan times (مواعيد رمضان): the wizard checks the dates itself, so
  // these are a second Ramadan for the year, or a school day already gone.
  ramadan_times_dates: "ramadan.errors.dates",
  ramadan_times_too_long: "ramadan.errors.tooLong",
  ramadan_times_past: "ramadan.errors.past",
  ramadan_times_exists: "ramadan.errors.exists",
  ramadan_times_overlap: "ramadan.errors.overlap",
  // The timetable (الجدول الدراسي): the page checks the same rules before it
  // saves, so these mean two writers raced.
  timetable_revision_conflict: "education.timetable.errors.revisionConflict",
  timetable_weekday_twice: "education.timetable.errors.weekdayTwice",
  timetable_bell_without_lessons: "education.timetable.errors.bellWithoutLessons",
  timetable_day_too_long: "education.timetable.errors.dayTooLong",
  timetable_unit_outside_day: "education.timetable.errors.unitOutsideDay",
  timetable_class_clash: "education.timetable.errors.classClash",
  timetable_teacher_clash: "education.timetable.errors.teacherClash",
  exam_period_revision_conflict: "education.exams.errors.revisionConflict",
  exam_period_dates_missing: "education.exams.errors.datesMissing",
  exam_period_dates_reversed: "education.exams.errors.datesReversed",
  exam_period_too_long: "education.exams.errors.tooLong",
  exam_day_outside_period: "education.exams.errors.dayOutsidePeriod",
  exam_sitting_off_day: "education.exams.errors.sittingOffDay",
  exam_sitting_past_midnight: "education.exams.errors.sittingPastMidnight",
  exam_period_empty: "education.exams.errors.empty",
  exam_section_clash: "education.exams.errors.sectionClash",
  exam_invigilator_clash: "education.exams.errors.invigilatorClash",
  exam_period_overlap_clash: "education.exams.errors.overlapClash",
  // The setup (إعداد فصول): subjects, teachers' accounts, who teaches what.
  subject_name_taken: "education.setup.errors.subjectNameTaken",
  subject_in_use: "education.setup.errors.subjectInUse",
  teacher_phone_used_by_parent: "education.setup.errors.teacherPhoneUsedByParent",
  assignment_teacher_invalid: "education.setup.errors.assignmentInvalid",
  assignment_subject_invalid: "education.setup.errors.assignmentInvalid",
  assignment_class_invalid: "education.setup.errors.assignmentInvalid",
  // Official holidays (العطل الرسمية): a date is sent once, a school answers once.
  official_holiday_already_sent: "officialHolidays.errors.alreadySent",
  official_holiday_not_sent: "officialHolidays.errors.notSent",
  official_holiday_invalid_rule: "officialHolidays.errors.invalidRule",
  official_holiday_invalid_dates: "officialHolidays.errors.invalidDates",
  official_holiday_name_from_rule: "officialHolidays.errors.nameFromRule",
  official_holiday_not_one_off: "officialHolidays.errors.notOneOff",
  official_holiday_unknown_school: "officialHolidays.errors.unknownSchool",
  official_holiday_no_schools: "officialHolidays.errors.noSchools",
  holiday_suggestion_passed: "officialHolidays.errors.passed",
  // Occasions (المناسبات): a celebration shown somewhere stays; references must exist.
  celebration_in_use: "occasions.errors.celebrationInUse",
  occasion_unknown_celebration: "occasions.errors.unknownCelebration",
  occasion_unknown_holiday: "occasions.errors.unknownHoliday",
  // Riders who track their own bus (الركاب) — utils/riders.js RIDER_ERROR_KEYS.
  riders_disabled: "riders.errors.disabled",
  rider_phone_taken: "riders.errors.phoneTaken",
  rider_exists: "riders.errors.exists",
  self_rider_fixed: "riders.errors.fixed",
  // Merging two journeys — views/manager/journey/mergeJourneys.js MERGE_ERROR_KEYS.
  journey_merge_disabled: "trips.merge.errors.disabled",
  merge_same_journey: "trips.merge.errors.sameJourney",
  merge_journey_inactive: "trips.merge.errors.inactive",
  merge_wrong_direction: "trips.merge.errors.wrongDirection",
  merge_run_in_progress: "trips.merge.errors.runInProgress",
  merge_not_ready: "trips.merge.errors.notReady",
  merge_supervisor_invalid: "trips.merge.errors.supervisorInvalid",
  merge_bus_invalid: "trips.merge.errors.busInvalid",
  // The Services page «الخدمات» — views/manager/services/.
  service_closed: "services.errors.closed",
  service_hidden: "services.errors.hidden",
  service_is_on: "services.errors.isOn",
  service_request_pending: "services.errors.requestPending",
  service_not_requestable: "services.errors.notRequestable",
  service_visibility_invalid: "services.errors.visibilityInvalid",
  service_has_no_settings: "services.errors.noSettings",
  // Polls «الاستطلاعات» — views/manager/polls/. The editor checks the same
  // limits first (pollForm.pollProblems); these mean the server judged again.
  polls_disabled: "polls.errors.disabled",
  poll_not_found: "polls.errors.notFound",
  poll_locked: "polls.errors.locked",
  poll_status_invalid: "polls.errors.statusInvalid",
  poll_dates_invalid: "polls.errors.datesInvalid",
  poll_tiles_need_images: "polls.errors.tilesNeedImages",
  poll_options_invalid: "polls.errors.optionsInvalid",
  poll_audience_invalid: "polls.errors.audienceInvalid",
  poll_link_host_not_allowed: "polls.errors.linkHostNotAllowed",
  poll_link_root_only: "polls.errors.linkRootOnly",
  poll_root_status_only: "polls.errors.rootStatusOnly",
  poll_paused_by_wosool: "polls.errors.pausedByWosool",
  poll_field_required: "polls.errors.fieldRequired",
};

/**
 * Refusals that arrive as 403 but are not a permissions problem: the school's
 * Polls switched off, a school's poll root may only pause or close, a pause
 * only Wosool lifts, a link only root attaches. Said in their own words
 * rather than "you don't have permission".
 */
const POLL_FORBIDDEN_CODES = new Set([
  "polls_disabled",
  "poll_root_status_only",
  "poll_paused_by_wosool",
  "poll_link_root_only",
]);

const mapErrorCode = (code?: unknown): string | null => {
  if (typeof code !== "string") return null;
  const key = ERROR_CODE_KEYS[code];
  return key ? i18n.t(key) : null;
};

const mapBackendErrorToTranslation = (backendError: string): string => {
  // Normalize the error message (trim and lowercase for comparison)
  const normalized = backendError.trim().toLowerCase();
  
  // Map common backend error messages to translation keys
  if (normalized === 'invalid credentials.' || normalized === 'invalid credentials') {
    return ErrorMessages.invalidCredentials();
  }
  
  if (normalized.includes('session expired') || normalized.includes('token expired')) {
    return ErrorMessages.sessionExpired();
  }
  
  if (normalized.includes('insufficient permissions') || normalized.includes('permission denied')) {
    return ErrorMessages.forbidden();
  }
  
  if (normalized.includes('not found')) {
    return ErrorMessages.notFound();
  }
  
  // If no mapping found, return the original error
  return backendError;
};

// API Base Paths
export const API_PATHS = {
  BILLING: "/api/v1/billing",
  FINANCE: "/api/v1/finance",
  IMPORT_EXPORT: "/api/v1/import-export",
  // Add other API paths as needed
} as const;

class ApiClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = process.env.REACT_APP_API_URL || "";
  }

  private getHeaders(): HeadersInit {
    return {
      "Content-Type": "application/json",
      Authorization: useAuthStore.getState().token
        ? `Bearer ${useAuthStore.getState().token}`
        : "",
    };
  }

  private async parseErrorBody(response: Response): Promise<any | null> {
    try {
      return await response.json();
    } catch {
      return null;
    }
  }

  private async request<T>(endpoint: string, config: RequestInit): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers = { ...this.getHeaders(), ...config.headers };

    try {
      const response = await fetch(url, { ...config, headers });

      if (!response.ok) {
        const errorData = await this.parseErrorBody(response);

        // Handle authentication and authorization errors differently
        if (response.status === 401) {
          // 401 Unauthorized: Token is invalid/expired
          // Don't redirect on login/auth endpoints to avoid infinite loops
          if (!isAuthEndpoint(endpoint)) {
            const showNotification = createNotificationHandler();

            // Use the enhanced token expiration handler
            handleTokenExpiration(showNotification);

            // Throw an error with a user-friendly translated message
            throw new ApiError(
              ErrorMessages.sessionExpired(),
              response.status,
              {
                errorId: errorData?.error_id,
                requestId: errorData?.request_id,
                timestamp: errorData?.timestamp,
                errorType: errorData?.error_type,
                details: errorData,
              }
            );
          }
        } else if (response.status === 403) {
          // 403 Forbidden: Valid token but insufficient permissions
          // Do NOT log out the user, just show an error message
          const showNotification = createNotificationHandler();

          // A finance section the school switched off is not a permissions
          // problem — say what actually happened instead of "no permission".
          // Same for making a bus move while the school has moves off, and
          // for adding a rider while the school's riders switch is off.
          // The finance usage policy not being accepted (yet, or again after
          // a new version) is not a permissions problem either: the finance
          // pages answer it with the acceptance screen, so say nothing here
          // and tell them to re-check.
          // Nor is a teacher's sign-in, which the backend refuses on this
          // client (`teacher_app_only`): say where teachers sign in, as the
          // login page does when it refuses one itself.
          const termsPending =
            errorData?.error_type === "FinanceTermsNotAcceptedException";
          const errorMessage =
            errorData?.error_type === "FinanceFeatureDisabledException"
              ? i18n.t(
                  "finance.featureDisabled",
                  "This section has been turned off for your school."
                )
              : errorData?.error_code === "child_moves_disabled"
                ? i18n.t("childMoves.errors.disabled")
                : errorData?.error_code === "riders_disabled"
                ? i18n.t("riders.errors.disabled")
                : errorData?.error_code === "journey_merge_disabled"
                ? i18n.t("trips.merge.errors.disabled")
                : errorData?.error_code === "teacher_app_only"
                  ? i18n.t("auth.teacher_blocked")
                  : errorData?.error_code === "education_disabled"
                    ? i18n.t("education.posts.disabled")
                    : errorData?.error_code === "service_closed" ||
                        errorData?.error_code === "service_hidden" ||
                        POLL_FORBIDDEN_CODES.has(errorData?.error_code)
                      ? mapErrorCode(errorData?.error_code) || ErrorMessages.forbidden()
                    : termsPending
                    ? mapErrorCode(errorData?.error_code) || ErrorMessages.forbidden()
                    : ErrorMessages.forbidden();
          if (termsPending) {
            if (typeof window !== "undefined") {
              window.dispatchEvent(new Event("finance:terms-required"));
            }
          } else {
            handlePermissionError(
              showNotification,
              errorMessage
            );
          }

          throw new ApiError(
            errorMessage,
            response.status,
            {
              errorId: errorData?.error_id,
              requestId: errorData?.request_id,
              timestamp: errorData?.timestamp,
              errorType: errorData?.error_type,
              details: errorData,
            }
          );
        } else if (response.status === 429) {
          // 429 Too Many Requests: Rate limit exceeded
          // Always use i18n message; backend detail is English-only (see errorData in details).
          const retryAfter = errorData.retry_after || 60;
          const message = ErrorMessages.rateLimitExceeded(retryAfter);
          
          // Create error with retry_after info for UI countdown
          const rateLimitError = new ApiError(message, response.status, {
            errorId: errorData?.error_id,
            requestId: errorData?.request_id,
            timestamp: errorData?.timestamp,
            errorType: errorData?.error_type,
            details: errorData,
          });
          (rateLimitError as any).retryAfter = retryAfter;
          logAppError(rateLimitError, {
            source: "apiClient",
            action: "http_error",
            endpoint,
            statusCode: response.status,
            errorId: rateLimitError.errorId,
            requestId: rateLimitError.requestId,
            errorType: rateLimitError.errorType,
            timestamp: rateLimitError.timestamp,
          });
          throw rateLimitError;
        }

        //TODO: Move this to helper function.
        let errorMessage: string = ErrorMessages.unexpectedError();

        // The code first: it is the contractual half of the refusal.
        const coded = mapErrorCode(errorData?.error_code);
        if (coded) {
          errorMessage = coded;
        } else if (typeof errorData?.message === "string") {
          errorMessage = mapBackendErrorToTranslation(errorData.message);
        } else if (Array.isArray(errorData?.detail)) {
          errorMessage = errorData.detail
            .map((err: any) => err.msg || ErrorMessages.validationError())
            .join("; ");
        } else if (typeof errorData?.detail === "string") {
          errorMessage = mapBackendErrorToTranslation(errorData.detail);
        } else if (errorData) {
          try {
            errorMessage = JSON.stringify(errorData);
          } catch {
            errorMessage = ErrorMessages.serverError();
          }
        } else {
          errorMessage = ErrorMessages.serverError();
        }
        const apiError = new ApiError(errorMessage, response.status, {
          errorId: errorData?.error_id,
          requestId: errorData?.request_id,
          timestamp: errorData?.timestamp,
          errorType: errorData?.error_type,
          details: errorData,
        });
        logAppError(apiError, {
          source: "apiClient",
          action: "http_error",
          endpoint,
          statusCode: response.status,
          errorId: apiError.errorId,
          requestId: apiError.requestId,
          errorType: apiError.errorType,
          timestamp: apiError.timestamp,
        });
        throw apiError;
      }

      return response.json() as Promise<T>;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      // Network error or client-side issue - use translated message
      const networkError = new ApiError(ErrorMessages.networkError(), 500);
      logAppError(error, {
        source: "apiClient",
        action: "network_or_client_error",
        endpoint,
        statusCode: 500,
      });
      throw networkError;
    }
  }

  public async get<T>(
    endpoint: string,
    params?: Record<string, any>
  ): Promise<T> {
    let query = "";
    if (params) {
      // Filter out undefined and null values before creating URLSearchParams
      const filteredParams = Object.fromEntries(
        Object.entries(params).filter(
          ([_, value]) => value !== undefined && value !== null && value !== ""
        )
      );
      if (Object.keys(filteredParams).length > 0) {
        query = `?${new URLSearchParams(filteredParams)}`;
      }
    }
    return this.request<T>(`${endpoint}${query}`, { method: "GET" });
  }

  public async post<T>(
    endpoint: string,
    body: any,
    isFormData?: boolean
  ): Promise<T> {
    const content = isFormData
      ? new URLSearchParams(body).toString()
      : JSON.stringify(body);
    const headers: HeadersInit = isFormData
      ? { "Content-Type": "application/x-www-form-urlencoded" }
      : { "Content-Type": "application/json" };

    return this.request<T>(endpoint, {
      method: "POST",
      body: content,
      headers: headers,
    });
  }

  public async put<T>(endpoint: string, body: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  }

  public async patch<T>(endpoint: string, body: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  }

  public async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: "DELETE",
    });
  }

  public async getBlob(
    endpoint: string,
    params?: Record<string, any>
  ): Promise<Blob> {
    const url = new URL(`${this.baseUrl}${endpoint}`);
    if (params) {
      Object.keys(params).forEach((key) => {
        if (
          params[key] !== undefined &&
          params[key] !== null &&
          params[key] !== ""
        ) {
          url.searchParams.append(key, params[key].toString());
        }
      });
    }

    const headers = this.getHeaders();
    const response = await fetch(url.toString(), { headers });

    if (!response.ok) {
      // Same message as before; `status` and the backend's `error_code` ride
      // along so a download page can say *why* (rate limit, too large).
      const error = new Error(
        `HTTP error! status: ${response.status}`
      ) as Error & { status?: number; errorCode?: string };
      error.status = response.status;
      try {
        const body = await response.json();
        error.errorCode = body?.error_code;
      } catch {
        // Not JSON — the status is all there is.
      }
      throw error;
    }

    return response.blob();
  }
}

class ApiError extends Error {
  public errorId?: string;
  public requestId?: string;
  public timestamp?: string;
  public errorType?: string;
  public details?: any;

  constructor(
    public message: string,
    public publicStatus: number,
    metadata?: {
      errorId?: string;
      requestId?: string;
      timestamp?: string;
      errorType?: string;
      details?: any;
    }
  ) {
    super(message);
    this.errorId = metadata?.errorId;
    this.requestId = metadata?.requestId;
    this.timestamp = metadata?.timestamp;
    this.errorType = metadata?.errorType;
    this.details = metadata?.details;
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

export const apiClient = new ApiClient();
